/* @flow */

import { root } from './conf'
import qs from 'query-string'
import http from 'axios'
import moment from 'moment'
import _ from 'lodash'

let changesCache = []

/**
 * Get list of changes for an element of a given typename and with a given id.
 *
 * @function
 * @memberof ChangelogService
 */
const getAllChanges = (id: string, typename: string, since?: Date) => {
  const params = since
    ? {
        typename: typename,
        identifier: id,
        since: moment(new Date()).diff(since, 'm'),
        limit: 10000
      }
    : {
        typename: typename,
        identifier: id
      }

  const url = `${root}/changes?${qs.stringify(params)}`

  return http.get(url).then(res => res.data)
}

/**
 * Get data for a specific change of an element of a given typename and given id.
 *
 * @function
 * @memberof ChangelogService
 */
const getSpecificChange = (id: string, changeId: string, typename: string) => {
  let change = _.find(changesCache, { identifier: changeId })
  if (!change) {
    const params = {
      typename: typename,
      identifier: id,
      change: changeId
    }
    const url = `${root}/change?${qs.stringify(params)}`

    return http.get(url).then(res => {
      if (!_.find(changesCache, { identifier: changeId })) {
        changesCache.push(res.data)
      }
      return _.cloneDeep(res.data)
    })
  } else {
    return Promise.resolve(_.cloneDeep(change))
  }
}

/**
 * Change a tagname for a change.
 *
 * @function
 * @memberof ChangelogService
 */
const tagChange = (id: string, changeId: string, tagname: string, typename: string) => {
  const body = {
    typename: typename,
    identifier: id,
    change: changeId,
    tagname: tagname
  }

  return http.post(`${root}/tag`, body)
}

/**
 * Get last registered change
 *
 * @function
 * @memberof ChangelogService
 */
const getLastChange = (id: string, typename: string) => {
  const params = {
    typename: typename,
    identifier: id,
    limit: 1,
    since: 5 * 365 * 24 * 60 // in the last ~5y
  }

  const url = `${root}/changes?${qs.stringify(params)}`

  return http.get(url).then(res => {
    if (res.data) return res.data[0] || {}
  })
}

/**
 * Get the login of the user who performed last change to the object with given id and typename
 *
 * @function
 * @memberof ChangelogService
 */
const getLastEditedBy = (id: string, typename: string): Promise<string> => {
  return getLastChange(id, typename).then(change => {
    return change && change.context && change.context.author
  })
}

const clearChangesCache = () => {
  changesCache = []
}

const self = {
  getAllChanges,
  getSpecificChange,
  getLastChange,
  tagChange,
  clearChangesCache,
  getLastEditedBy
}

/**
 * Changelog service: Interface for changelog related operations.
 * @module ChangelogService
 * @namespace ChangelogService
 * @version 1.0.0
 */
export default self
